import React, { useEffect, useState } from 'react';
import { useVibrancy } from '../VibrancyDashboardContext';
import { VibrancyProfileService } from '../vibrancyProfileService';
import { Avatar, HeaderIcon, cardHeaderStyle, cardStyle } from './shared';

const AVATAR_PALETTE = [
    { bg: '#fef3c7', color: '#92400e' },
    { bg: '#d1fae5', color: '#065f46' },
    { bg: '#cffafe', color: '#0891b2' },
    { bg: '#ebf2ff', color: '#1b52c4' },
    { bg: '#ede9fe', color: '#7c3aed' },
];

function getInitials(name: string) {
    return (name || 'A').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

interface Profile {
    studentId: string;
    name: string;
    passOutYear: number | null;
    city: string | null;
    country: string | null;
    designation: string | null;
    companyName: string | null;
    latestInstitution: string | null;
    profilePicture: string | null;
}

const SkeletonCard: React.FC = () => (
    <div className="border rounded-3 p-3 h-100 placeholder-glow" style={{ borderColor: '#e1e8f5', background: '#f4f6fb' }}>
        <div className="d-flex align-items-center gap-2 mb-2">
            <span className="placeholder rounded-circle flex-shrink-0" style={{ width: 36, height: 36, display: 'block' }} />
            <div className="flex-grow-1">
                <span className="placeholder col-8 d-block mb-1" style={{ height: 14 }} />
                <span className="placeholder col-6 d-block" style={{ height: 11 }} />
            </div>
        </div>
        <span className="placeholder col-10 d-block mb-1" style={{ height: 12 }} />
        <span className="placeholder col-7 d-block" style={{ height: 11 }} />
    </div>
);

/** VD-09 Notable Alumni featured profiles grid (1/2/3-col reflow). */
const NotableAlumni: React.FC = () => {
    const { version, selectedInstituteId, instituteLoaded } = useVibrancy();

    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [loading,  setLoading]  = useState(false);

    useEffect(() => {
        if (!selectedInstituteId) { setProfiles([]); return; }
        setLoading(true);
        VibrancyProfileService.getNotableAlumni(selectedInstituteId)
            .then(r  => setProfiles(r.data?.data || []))
            .catch(() => setProfiles([]))
            .finally(() => setLoading(false));
    }, [selectedInstituteId]);

    if (!instituteLoaded) return null;

    const colClass = profiles.length >= 3 ? 'col-md-4' : profiles.length === 2 ? 'col-md-6' : 'col-md-12';

    return (
        <div className="card mb-3" style={cardStyle}>
            <div className="card-header bg-white d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
                <div className="fw-medium d-flex align-items-center gap-2 font-14">
                    <HeaderIcon icon="🌟" bg="#fce7f3" color="#db2777" />Notable Alumni — Featured Profiles
                </div>
              
            </div>
            <div className="card-body">
                {loading ? (
                    <div className="row g-2">
                        {[0, 1, 2].map(i => (
                            <div key={i} className="col-md-4"><SkeletonCard /></div>
                        ))}
                    </div>
                ) : profiles.length === 0 ? (
                    <div className="text-center text-muted py-4 small">
                        No notable alumni profiles identified. Mark alumni as notable to feature them here.
                    </div>
                ) : (
                    <div className="row g-2">
                        {profiles.map((p, i) => {
                            const av       = AVATAR_PALETTE[i % AVATAR_PALETTE.length];
                            const initials = getInitials(p.name);
                            const meta     = [p.passOutYear ? `Class of ${p.passOutYear}` : null, p.city, p.country].filter(Boolean).join(' · ');
                            const role     = [p.designation, p.companyName].filter(Boolean).join(' @ ');
                            return (
                                <div key={p.studentId || i} className={colClass}>
                                    <div className="border rounded-3 p-3 h-100"
                                        style={{ borderColor: '#e1e8f5', background: '#f4f6fb' }}>

                                        {/* Avatar + name */}
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <Avatar initials={initials} bg={av.bg} color={av.color} size={36} />
                                            <div style={{ minWidth: 0 }}>
                                                <div className="fw-medium font-14 text-truncate">{p.name}</div>
                                                {meta && <div className="font-12 text-secondary text-truncate">{meta}</div>}
                                            </div>
                                        </div>

                                        {/* Role */}
                                        {role && (
                                            <div className="fw-medium font-13 text-secondary-emphasis mb-1 text-truncate">
                                                💼 {role}
                                            </div>
                                        )}

                                        {/* Latest institution */}
                                        {p.latestInstitution && (
                                            <div className="font-12 text-secondary text-truncate">
                                                🎓 {p.latestInstitution}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotableAlumni;
